import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface User {
  id: string
  email: string
  name: string
  role: 'iso'
  status: 'active' | 'pending' | 'suspended' | 'rejected'
  lastLogin?: string
  createdAt: string
  updatedAt: string
}

interface ISO {
  id: string
  userId: string
  companyName: string
  contactInfo: any
  businessInfo?: any
  applicationStatus: 'pending' | 'approved' | 'rejected' | 'suspended'
  salesforceAccountId?: string
  approvedBy?: string
  approvedAt?: string
  createdAt: string
  updatedAt: string
  underwritingOwnerSfId?: string
}

interface ISOWithUser {
  user: User
  iso: ISO
}

interface SalesforceUser {
  id: string
  name: string
  email: string
}

const ISOManager: React.FC = () => {
  const [isos, setIsos] = useState<ISOWithUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState({
    status: 'all',
    applicationStatus: 'all',
    search: ''
  })
  const [salesforceUsers, setSalesforceUsers] = useState<SalesforceUser[]>([])
  const [loadingSalesforceUsers, setLoadingSalesforceUsers] = useState(false)
  const [editingOwner, setEditingOwner] = useState<string | null>(null)
  const [selectedOwner, setSelectedOwner] = useState<string>('')
  const [savingOwner, setSavingOwner] = useState<string | null>(null)
  const navigate = useNavigate()

  const fetchISOs = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/admin/isos', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setIsos(data.isos || [])
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to fetch ISOs')
      }
    } catch (error) {
      console.error('Error fetching ISOs:', error)
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchISOs()
    fetchSalesforceUsers() // Fetch Salesforce users on component mount
    debugISOs() // Debug function to check ISO data
  }, [])

  const debugISOs = async () => {
    try {
      const response = await fetch('/api/admin/debug/isos', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('🔍 Debug ISO Data from DB:', data.debug_data)
      }
    } catch (error) {
      console.error('Debug query failed:', error)
    }
  }

  const filteredISOs = isos.filter(item => {
    const matchesStatus = filter.status === 'all' || item.user.status === filter.status
    const matchesApplicationStatus = filter.applicationStatus === 'all' || 
      item.iso.applicationStatus === filter.applicationStatus
    const matchesSearch = filter.search === '' || 
      item.user.email.toLowerCase().includes(filter.search.toLowerCase()) ||
      item.user.name.toLowerCase().includes(filter.search.toLowerCase()) ||
      item.iso.companyName?.toLowerCase().includes(filter.search.toLowerCase())
    
    return matchesStatus && matchesApplicationStatus && matchesSearch
  })

  const getStatusBadge = (status: string) => {
    const badgeClass = {
      active: 'bg-success',
      pending: 'bg-warning',
      suspended: 'bg-danger',
      rejected: 'bg-secondary'
    }[status] || 'bg-secondary'

    return <span className={`badge ${badgeClass}`}>{status.toUpperCase()}</span>
  }

  const getApplicationStatusBadge = (status: string) => {
    const badgeClass = {
      approved: 'bg-success',
      pending: 'bg-warning',
      rejected: 'bg-danger',
      suspended: 'bg-secondary'
    }[status] || 'bg-secondary'

    return <span className={`badge ${badgeClass}`}>{status.toUpperCase()}</span>
  }

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatShortDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const handleViewDeals = (isoId: string) => {
    navigate(`/admin/iso/${isoId}`)
  }

  const fetchSalesforceUsers = async () => {
    try {
      setLoadingSalesforceUsers(true)
      const response = await fetch('/api/admin/salesforce/users', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSalesforceUsers(data.users || [])
        console.log('✅ Fetched Salesforce users:', data.users?.length || 0)
      } else {
        const errorData = await response.json()
        console.error('Failed to fetch Salesforce users:', errorData.message)
        setError(`Failed to fetch Salesforce users: ${errorData.message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error fetching Salesforce users:', error)
      setError('Network error while fetching Salesforce users. Please try again.')
    } finally {
      setLoadingSalesforceUsers(false)
    }
  }

  const handleEditOwner = (isoId: string, currentOwnerId?: string) => {
    console.log('✏️ Editing owner for ISO:', { isoId, currentOwnerId, salesforceUsersCount: salesforceUsers.length })
    setEditingOwner(isoId)
    setSelectedOwner(currentOwnerId || '')
    // Only fetch if we don't have users and aren't already loading
    if (salesforceUsers.length === 0 && !loadingSalesforceUsers) {
      console.log('📡 Fetching Salesforce users...')
      fetchSalesforceUsers()
    }
  }

  const handleOwnerChange = async (isoId: string, newOwnerId: string) => {
    console.log('🔄 Auto-saving owner change:', { isoId, newOwnerId })
    console.log('🔍 ISO object for debugging:', isos.find(item => item.iso.id === isoId))
    setSelectedOwner(newOwnerId)
    
    if (!newOwnerId) {
      // If they selected "Select owner...", don't save yet
      return
    }

    // Auto-save the change - pass the newOwnerId directly
    await handleSaveOwner(isoId, newOwnerId)
  }

  const handleSaveOwner = async (isoId: string, ownerIdToSave?: string) => {
    const ownerToSave = ownerIdToSave || selectedOwner
    console.log('🔄 Saving underwriting owner:', { isoId, ownerToSave, selectedOwner })
    
    if (!ownerToSave) {
      setError('Please select an underwriting owner')
      return
    }

    setSavingOwner(isoId) // Set saving state

    try {
      const response = await fetch(`/api/admin/isos/${isoId}/underwriting-owner`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          underwritingOwnerId: ownerToSave
        })
      })

      console.log('📡 API Response status:', response.status)

      if (response.ok) {
        const responseData = await response.json()
        console.log('✅ Successfully updated underwriting owner:', responseData)
        
        // Update the local state
        setIsos(prevIsos => 
          prevIsos.map(item => 
            item.iso.id === isoId 
              ? { ...item, iso: { ...item.iso, underwritingOwnerSfId: ownerToSave } }
              : item
          )
        )
        setEditingOwner(null)
        setSelectedOwner('')
        
        // Clear any previous errors
        setError('')
      } else {
        const errorData = await response.json()
        console.error('❌ API Error:', errorData)
        setError(errorData.message || 'Failed to update underwriting owner')
      }
    } catch (error) {
      console.error('❌ Network error updating underwriting owner:', error)
      setError('Network error. Please try again.')
    } finally {
      setSavingOwner(null) // Clear saving state
    }
  }

  const handleCancelEdit = () => {
    setEditingOwner(null)
    setSelectedOwner('')
  }

  const getUnderwritingOwnerName = (ownerId?: string) => {
    if (!ownerId) return 'Not assigned'
    
    // If Salesforce users are still loading, show loading state
    if (loadingSalesforceUsers && salesforceUsers.length === 0) {
      return 'Loading...'
    }
    
    const user = salesforceUsers.find(u => u.id === ownerId)
    return user ? user.name : `User ID: ${ownerId.substring(0, 8)}...`
  }

  return (
    <>
      {/* Page Header */}
      <div className="admin-content-header">
        <h2 className="admin-page-title">ISO Management</h2>
        <button 
          className="btn btn-outline-primary"
          onClick={fetchISOs}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Refreshing...
            </>
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      {/* Page Description */}
      <div className="px-4 py-3 bg-light border-bottom">
        <p className="mb-0 text-muted">
          Manage ISO users and their associated company information. View user details, company profiles, and application status.
        </p>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, or company..."
                value={filter.search}
                onChange={(e) => setFilter({...filter, search: e.target.value})}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">User Status</label>
              <select 
                className="form-select"
                value={filter.status}
                onChange={(e) => setFilter({...filter, status: e.target.value})}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Application Status</label>
              <select 
                className="form-select"
                value={filter.applicationStatus}
                onChange={(e) => setFilter({...filter, applicationStatus: e.target.value})}
              >
                <option value="all">All Applications</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Statistics</label>
              <div className="d-flex gap-3">
                <small className="text-muted">
                  Total ISOs: <strong>{isos.length}</strong>
                </small>
                <small className="text-muted">
                  Active: <strong>{isos.filter(item => item.user.status === 'active').length}</strong>
                </small>
                <small className="text-muted">
                  Companies: <strong>{isos.length}</strong>
                </small>
                <small className="text-muted">
                  Approved: <strong>{isos.filter(item => item.iso.applicationStatus === 'approved').length}</strong>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* ISOs Table */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading ISOs...</p>
            </div>
          ) : filteredISOs.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">No ISOs found</h5>
              <p className="text-muted">
                {filter.search || filter.status !== 'all' || filter.applicationStatus !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'No ISO companies have been registered yet'
                }
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ padding: '12px 8px', minWidth: '120px' }}>Company</th>
                    <th style={{ padding: '12px 8px', minWidth: '100px' }}>Name</th>
                    <th style={{ padding: '12px 8px', minWidth: '150px' }}>Email</th>
                    <th style={{ padding: '12px 8px', minWidth: '140px' }}>Assigned Rep</th>
                    <th style={{ padding: '12px 8px', minWidth: '80px' }}>Status</th>
                    <th style={{ padding: '12px 8px', minWidth: '120px' }}>App Status</th>
                    <th style={{ padding: '12px 8px', minWidth: '100px' }}>Last Login</th>
                    <th style={{ padding: '12px 8px', minWidth: '90px' }}>Created</th>
                    <th style={{ padding: '12px 8px', minWidth: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredISOs.map((item) => (
                    <React.Fragment key={item.user.id}>
                      <tr>
                        <td style={{ padding: '12px 8px' }}>
                          <div>
                            <strong>{item.iso.companyName}</strong>
                            {item.iso.salesforceAccountId && (
                              <small className="d-block text-muted">
                                SF: {item.iso.salesforceAccountId}
                              </small>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <strong>{item.user.name}</strong>
                        </td>
                        <td style={{ padding: '12px 8px' }}>{item.user.email}</td>
                        <td style={{ padding: '12px 8px' }}>
                          {editingOwner === item.iso.id ? (
                            <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'nowrap' }}>
                              {loadingSalesforceUsers ? (
                                <div className="d-flex align-items-center">
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  <span className="text-muted">Loading...</span>
                                </div>
                              ) : (
                                <>
                                  <select
                                    className="form-select form-select-sm"
                                    value={selectedOwner}
                                    onChange={(e) => handleOwnerChange(item.iso.id, e.target.value)}
                                    style={{ minWidth: '150px' }}
                                    disabled={savingOwner === item.iso.id}
                                  >
                                    <option value="">Select owner...</option>
                                    {salesforceUsers.map(user => (
                                      <option key={user.id} value={user.id}>
                                        {user.name}
                                      </option>
                                    ))}
                                  </select>
                                  {savingOwner === item.iso.id && (
                                    <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" style={{ width: '16px', height: '16px' }}></span>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleCancelEdit}
                                    title="Cancel"
                                    style={{ minWidth: '32px', fontWeight: 'bold' }}
                                    disabled={savingOwner === item.iso.id}
                                  >
                                    ✕
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span
                              className="text-primary"
                              style={{ cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => handleEditOwner(item.iso.id, item.iso.underwritingOwnerSfId)}
                            >
                              {loadingSalesforceUsers && salesforceUsers.length === 0 && item.iso.underwritingOwnerSfId ? (
                                <span className="text-muted">
                                  <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '12px', height: '12px' }}></span>
                                  Loading...
                                </span>
                              ) : (
                                getUnderwritingOwnerName(item.iso.underwritingOwnerSfId)
                              )}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px' }}>{getStatusBadge(item.user.status)}</td>
                        <td style={{ padding: '12px 8px' }}>
                          {getApplicationStatusBadge(item.iso.applicationStatus)}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          {item.user.lastLogin ? (
                            <small className="text-muted">
                              {formatDate(item.user.lastLogin)}
                            </small>
                          ) : (
                            <small className="text-muted">Never</small>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <small className="text-muted">
                            {formatShortDate(item.user.createdAt)}
                          </small>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <button 
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleViewDeals(item.user.id)}
                            style={{ fontSize: '0.8rem' }}
                          >
                            <i className="bi bi-graph-up me-1"></i>
                            View Deals
                          </button>
                        </td>
                      </tr>

                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ISOManager
